import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Globe } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import TranslateButton from './TranslateButton';

const LANG_LABELS = {
  es: 'Spanish (Español)',
  fr: 'French (Français)',
  pt: 'Portuguese (Português)',
};

/**
 * Generic translations editor.
 *
 * Props:
 *   translations: Record<langCode, Record<fieldKey, any>>   — current translations object
 *   onChange: (newTranslations) => void
 *   sourceContent: Record<fieldKey, any>                    — English source for AI translation
 *   fields: Array<{ key, label, type? ('text'|'textarea') }> — fields to expose for editing
 *   languages: string[]                                     — languages to support, default ['es']
 */
const TranslationsEditor = ({
  translations = {},
  onChange,
  sourceContent,
  fields = [],
  languages = ['es'],
}) => {
  const [openLang, setOpenLang] = useState(null);

  const setField = (lang, key, value) => {
    const next = {
      ...translations,
      [lang]: { ...(translations[lang] || {}), [key]: value },
    };
    onChange(next);
  };

  const handleTranslated = (lang, esJson) => {
    onChange({ ...translations, [lang]: { ...(translations[lang] || {}), ...esJson } });
  };

  return (
    <div className="border border-maroon/15 rounded-sm bg-sand/20 p-4 space-y-3" data-testid="translations-editor">
      <div className="flex items-center gap-2 text-maroon">
        <Globe size={18} className="text-terracotta" />
        <span className="font-semibold">Translations</span>
        <span className="text-xs text-maroon/50">(optional — defaults to English when missing)</span>
      </div>

      {languages.map((lang) => {
        const isOpen = openLang === lang;
        const t = translations[lang] || {};
        const filledCount = fields.filter((f) => {
          const v = t[f.key];
          return v !== undefined && v !== null && v !== '';
        }).length;

        return (
          <div key={lang} className="border border-maroon/10 rounded-sm bg-white">
            <button
              type="button"
              onClick={() => setOpenLang(isOpen ? null : lang)}
              className="w-full flex items-center justify-between p-3 text-left hover:bg-sand/30"
              data-testid={`translation-toggle-${lang}`}
            >
              <span className="font-semibold text-maroon flex items-center gap-2">
                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                {LANG_LABELS[lang] || lang.toUpperCase()}
              </span>
              <span className="text-xs text-maroon/50">
                {filledCount} / {fields.length} fields filled
              </span>
            </button>

            {isOpen && (
              <div className="px-4 pb-4 space-y-3 border-t border-maroon/10 pt-3">
                <div className="flex justify-end">
                  <TranslateButton
                    sourceContent={sourceContent}
                    onTranslated={(esJson) => handleTranslated(lang, esJson)}
                    targetLang={lang}
                    targetLabel={(LANG_LABELS[lang] || lang).split(' ')[0]}
                    testId={`translate-${lang}-btn`}
                  />
                </div>

                {fields.map((f) => {
                  const value = t[f.key] ?? '';
                  return (
                    <div key={f.key} className="space-y-1">
                      <Label className="text-xs">{f.label}</Label>
                      {f.type === 'textarea' ? (
                        <Textarea
                          value={typeof value === 'string' ? value : JSON.stringify(value)}
                          onChange={(e) => setField(lang, f.key, e.target.value)}
                          rows={3}
                          className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
                          data-testid={`translation-${lang}-${f.key}`}
                        />
                      ) : (
                        <Input
                          value={typeof value === 'string' ? value : ''}
                          onChange={(e) => setField(lang, f.key, e.target.value)}
                          className="input-field"
                          data-testid={`translation-${lang}-${f.key}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TranslationsEditor;
